import config from '@/config';
import { ApiBase, InternationalizedField, ListOptions } from '@/api/common';
import ApiList from '@/api/list';
import OutcomeFeedback from '@/api/outcomeFeedback';
import Exercise from '@/api/exercise';
import LockManager from '@/models/lock';
import Video from '@/api/video';

const NO_UPLOAD_FIELDS = [
  'module_id',
  'submodule_id',
  'resource_path',
  'exercises',
  'bg_image_link',
  'image_link',
  'created',
  'updated',
  'lesson_id',
  'video_objects',
  'outcome_feedback'
];

export default class Lesson extends ApiBase<Lesson> {
  public static readonly SLUG: string = 'lesson';
  private static readonly URL_BASE: string = config.API_ROOT + '/lesson/';

  // Initializing these fields (even with undefined)
  // is necessary, so hasOwnAttribute can find them.
  public module_id?: string = undefined;
  public submodule_id?: string = undefined;
  public resource_path?: string = undefined;
  public exercises?: string = undefined;
  public bg_image_path?: number | null = undefined;
  public bg_image_link?: string | null = undefined;
  public image_path?: number | null = undefined;
  public image_link?: string | null = undefined;
  public created?: number = undefined;
  public updated?: number = undefined;
  public lesson_id?: string = undefined;
  public phoneme?: string = undefined;
  public difficulty_level?: string = 'EASY';
  public game_type?: string = undefined;
  public game_subtype?: string = undefined;
  public speech_type?: string = undefined;
  public access?: string = 'membership';
  public version?: string = undefined;
  public order?: number = undefined;
  public parent_submodule?: number = undefined;
  public parent_module?: number = undefined;
  public title: InternationalizedField<string> = new InternationalizedField<string>();
  public name: InternationalizedField<string> = new InternationalizedField<string>();
  public description: InternationalizedField<string> = new InternationalizedField<string>();
  public outcome_feedback?: OutcomeFeedback = undefined;
  public metric_names?: InternationalizedField<string>[] = [];
  public reference_score?: number = undefined;
  public theme?: number = undefined;
  public video_objects?: Video[] = [];
  public video?: number = undefined;
  public reading_text?: string = undefined;
  public transcription?: string = undefined;
  public recommendable: boolean = undefined;
  public zoom_call: boolean = undefined;
  public first_exercise_title: string = undefined;
  public start_conversation_index?: number = undefined;
  public end_conversation_index?: number = undefined;
  public tags: string[] = [];

  //specific to program
  public is_challenge_lesson?: boolean = false;

  // currently unused
  public spreadsheet?: string = undefined;

  public constructor() {
    super({
      urlBase: Lesson.URL_BASE,
      filteredFields: NO_UPLOAD_FIELDS
    });

    this.outcome_feedback = new OutcomeFeedback();
    this.metric_names = [];
  }

  public assign(response: any): Lesson {
    super.assign(response);

    if (response) {
      if ('name' in response) {
        this.name = new InternationalizedField<string>(response.name);
      }
      if ('description' in response) {
        this.description = new InternationalizedField<string>(
          response.description
        );
      }
      if ('title' in response) {
        this.title = new InternationalizedField<string>(response.title);
      }
      if ('outcome_feedback' in response) {
        this.outcome_feedback = new OutcomeFeedback().assign(
          response.outcome_feedback
        );
      }
      if ('metric_names' in response) {
        this.metric_names = [];
        response.metric_names.forEach((metric_name: any) => {
          this.metric_names.push(
            new InternationalizedField<string>(metric_name)
          );
        });
      }
      if ('video' in response) {
        this.video = undefined;
        this.video_objects.push(new Video().assign(response.video));
      }
    }

    return this;
  }

  public static async get(id: number): Promise<Lesson> {
    return this.doGet({
      urlBase: Lesson.URL_BASE,
      id,
      create: (response) => new Lesson().assign(response)
    });
  }

  public static async list(): Promise<ApiList<Lesson>> {
    const options: ListOptions<Lesson> = {
      urlBase: Lesson.URL_BASE,
      create: (response) => new Lesson().assign(response)
    };
    return this.doList(options);
  }

  public static async getLessonsOfModule(id: number): Promise<ApiList<Lesson>> {
    const options: ListOptions<Lesson> = {
      urlBase: Lesson.URL_BASE,
      create: (response) => new Lesson().assign(response),
      all: true,
      parameters: {
        parent_module: id,
        minimal: true
      }
    };
    return this.doList(options);
  }

  public static async getLessonsOfSubModule(
    id: number
  ): Promise<ApiList<Lesson>> {
    const options: ListOptions<Lesson> = {
      urlBase: Lesson.URL_BASE,
      create: (response) => new Lesson().assign(response),
      all: true,
      parameters: {
        parent_submodule: id,
        minimal: true
      }
    };
    return this.doList(options);
  }

  public async saveMetricNames() {
    if (this.metric_names && this.metric_names.length === 0) {
      if (!this.id) {
        return delete this.metric_names;
      } else {
        this.metric_names = [];
      }
    }
  }

  public async save(): Promise<number> {
    await this.saveMetricNames();
    if (!this.id) {
      return await super.save();
    } else {
      await Promise.all([this.saveVideos()]);

      return await super.save();
    }
  }

  public getName(): string {
    const name = this.name.getDefaultValue();
    const title = this.title.getDefaultValue();

    if (name && title) {
      return name + ' - ' + title;
    } else if (name) {
      return name;
    } else if (title) {
      return title;
    }
    return '';
  }

  public async saveVideos() {
    for (const vid of this.video_objects) {
      if (vid.state === 'updated') {
        vid.parent_id = this.id;
        vid.parent_model = 'lesson';
        if (!this.video) {
          this.video = undefined;
        }
        this.video = await vid.save();
      } else if (vid.state === 'deleted' && vid.id) {
        await vid.delete();
      }
    }
  }

  public getFormattedDate(): string {
    if (this.updated === undefined) {
      return 'N/A';
    }
    const index = this.updated.toLocaleString().indexOf('.');
    return this.updated.toLocaleString().substring(0, index);
  }

  public async getReferenceScore() {
    let lockManager = new LockManager(Lesson.SLUG, this.id);
    await lockManager.tryAcquireLock();

    let lessonExercises: ApiList<Exercise> = await Exercise.getExercisesOfLesson(
      this.id
    );
    let nEx = 0;
    this.reference_score = 0;
    let num = 0;
    let den = 0;
    for (const ex of lessonExercises.results) {
      let exercise = await Exercise.get(ex.id);
      if (exercise.speaking_content.reference_score) {
        num +=
          exercise.speaking_content.reference_score *
          exercise.speaking_content.sentence.length;
        den += exercise.speaking_content.sentence.length;
        nEx++;
      }
    }
    if (den > 0) {
      this.reference_score = num / den;
    }
    await this.save();
    await lockManager.releaseLock();
  }
}
